import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api, getErrorMessage, type TenantCreateResponse } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { pathForTenant } from "../../contexts/TenantContext.tsx";
import TenantAvatar from "../TenantAvatar.tsx";
import TenantProfileFields, {
  MAX_OFFERING_SENTENCES,
  profilePayload,
  profileValues,
  sentenceCount,
  type TenantProfileValues,
} from "./TenantProfileFields.tsx";
import DesignedWorkflows from "./DesignedWorkflows.tsx";
import TenantResearchProgress, {
  profileReady,
  useTenantProfileRun,
} from "./TenantResearchProgress.tsx";

type Phase = "company" | "research" | "review";

const STEPS = ["Company", "Research", "Review"];
const PHASE_STEP: Record<Phase, number> = { company: 0, research: 1, review: 2 };

interface TenantOnboardingDialogProps {
  onClose: () => void;
  organizationId?: string;
}

/**
 * Create a tenant as a short onboarding: name + domain, then a research agent reads
 * the company's site (progress bar + what it is doing), then the drafted profile is
 * reviewed and saved. The agent writes into the tenant's empty fields itself, so
 * closing the dialog early keeps what it finds.
 */
export default function TenantOnboardingDialog({
  onClose,
  organizationId,
}: TenantOnboardingDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();
  const [phase, setPhase] = useState<Phase>("company");
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [created, setCreated] = useState<TenantCreateResponse | null>(null);
  const [values, setValues] = useState<TenantProfileValues>(profileValues(null));
  const filledFromTenant = useRef(false);
  const tenantId = created?.id;

  const invalidateTenants = () => {
    queryClient.invalidateQueries({ queryKey: keys.tenant.all });
    queryClient.invalidateQueries({ queryKey: keys.organizationTenant.all });
  };

  const create = useMutation({
    mutationFn: api.createTenant,
    // Shown inline above the form.
    meta: { suppressErrorToast: true },
    onSuccess: (tenant) => {
      setCreated(tenant);
      invalidateTenants();
      if (tenant.profile_run) {
        queryClient.setQueryData(keys.tenant.profileRun(tenant.id), tenant.profile_run);
        setPhase("research");
      } else {
        setPhase("review");
      }
    },
  });

  const run = useTenantProfileRun(phase === "company" ? null : tenantId);
  const runStatus = run.data?.status;

  const tenant = useQuery({
    queryKey: keys.tenant.detail(tenantId ?? ""),
    queryFn: () => api.getTenant(tenantId!),
    enabled: !!tenantId && phase === "review",
  });

  const ready = profileReady(run.data);

  // Profile saved: on to the review with what it filled in (workflows may still
  // be designed; the review shows that).
  useEffect(() => {
    if (phase === "research" && ready) {
      queryClient.invalidateQueries({ queryKey: keys.tenant.detail(tenantId!) });
      invalidateTenants();
      setPhase("review");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, ready]);

  // The review starts from the tenant as saved (the agent's draft), once.
  useEffect(() => {
    if (phase === "review" && tenant.data && !tenant.isFetching && !filledFromTenant.current) {
      filledFromTenant.current = true;
      setValues(profileValues(tenant.data));
    }
  }, [phase, tenant.data, tenant.isFetching]);

  const retry = useMutation({
    mutationFn: () => api.startTenantProfileResearch(tenantId!),
    onSuccess: (newRun) => queryClient.setQueryData(keys.tenant.profileRun(tenantId!), newRun),
  });

  const uploadLogo = useMutation({
    mutationFn: (file: File) => api.uploadTenantLogo(tenantId!, file),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.tenant.detail(tenantId!), updated);
      invalidateTenants();
    },
  });

  // Into the new tenant, when it belongs to the organization being worked in.
  const openTenant = async () => {
    if (!created) return;
    if (organizationId && currentOrg?.id === organizationId) {
      await queryClient.refetchQueries({ queryKey: keys.tenant.all });
      navigate(pathForTenant(created.slug));
    }
  };

  const save = useMutation({
    mutationFn: () => api.updateTenantIcp(tenantId!, profilePayload(values)),
    onSuccess: async () => {
      invalidateTenants();
      toast.success("Tenant ready");
      await openTenant();
      onClose();
    },
  });

  const closeInBackground = async () => {
    await openTenant();
    onClose();
  };

  const submitCompany = () => {
    const trimmed = name.trim();
    if (!trimmed || !organizationId || create.isPending) return;
    create.mutate({
      name: trimmed,
      organization_id: organizationId,
      domain: domain.trim() || null,
    });
  };

  const offeringSentences = sentenceCount(values.offering_text);
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <Dialog
      open
      // A click outside must not throw away a half-done onboarding.
      onClose={phase === "company" ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add tenant</DialogTitle>
      <DialogContent>
        <Stepper activeStep={PHASE_STEP[phase]} alternativeLabel sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {phase === "company" && (
          <Stack spacing={2}>
            <TextField
              autoFocus
              label="Company name"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCompany();
              }}
            />
            <TextField
              label="Website domain"
              placeholder="acme.com"
              fullWidth
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCompany();
              }}
              helperText="We research the company's website to set its icon and draft its profile. Optional."
            />
            {create.isError && <Alert severity="error">{getErrorMessage(create.error)}</Alert>}
          </Stack>
        )}

        {phase === "research" && (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Researching <strong>{created?.domain}</strong>: what {created?.name} sells, whom
              it sells to, and its icon. This usually takes a minute or two; then workflows are
              drafted from it.
            </Typography>
            <TenantResearchProgress run={run.data ?? undefined} />
          </Stack>
        )}

        {phase === "review" && (
          <Stack spacing={2.5}>
            {created?.profile_research_unavailable && (
              <Alert severity="info">{created.profile_research_unavailable}</Alert>
            )}
            {run.data?.status === "failed" && (
              <Alert severity="warning">{run.data.error ?? "The research failed."}</Alert>
            )}
            {ready && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Drafted from {run.data?.domain}. Check it and correct anything that is off: the
                prospecting and research agents work from it.
              </Typography>
            )}
            {run.data?.output?.notes && (
              <Alert severity="info">
                {run.data.output.notes}
              </Alert>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TenantAvatar
                name={tenant.data?.name ?? created?.name ?? ""}
                logoUrl={tenant.data?.logo_url}
                size={56}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap>
                  {tenant.data?.name ?? created?.name}
                </Typography>
                <Typography variant="body2" noWrap sx={{ color: "text.secondary" }}>
                  {tenant.data?.domain ?? created?.domain ?? "No domain"}
                </Typography>
              </Box>
              <input
                ref={fileInput}
                type="file"
                hidden
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadLogo.mutate(file);
                  e.target.value = "";
                }}
              />
              <Button
                size="small"
                variant="outlined"
                loading={uploadLogo.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {tenant.data?.logo_url ? "Replace icon" : "Upload icon"}
              </Button>
            </Box>
            <TenantProfileFields
              values={values}
              onChange={setValues}
              disabled={tenant.isLoading || save.isPending}
            />
            <DesignedWorkflows run={run.data ?? undefined} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {phase === "company" && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              loading={create.isPending}
              disabled={!name.trim() || !organizationId}
              onClick={submitCompany}
            >
              {domain.trim() ? "Create and research" : "Create"}
            </Button>
          </>
        )}
        {phase === "research" && (
          <>
            <Button onClick={closeInBackground}>Continue in background</Button>
            {runStatus === "failed" && (
              <>
                <Button onClick={() => setPhase("review")}>Fill in manually</Button>
                <Button variant="contained" loading={retry.isPending} onClick={() => retry.mutate()}>
                  Try again
                </Button>
              </>
            )}
          </>
        )}
        {phase === "review" && (
          <>
            <Button onClick={closeInBackground}>Skip for now</Button>
            <Button
              variant="contained"
              loading={save.isPending}
              disabled={tenant.isLoading || offeringSentences > MAX_OFFERING_SENTENCES}
              onClick={() => save.mutate()}
            >
              Finish
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
